/* eslint-disable no-undef */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Select, Spin, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { ChevronDown } from 'lucide-react';
import { useDebouncedValue } from '@client/hooks/useDebouncedValue';
import { useIsMobile } from '@client/hooks/useIsMobile';
import { MobileSelectSheet } from '@client/components/mobile';
import { useCascadingOptions } from '../hooks/useCascadingOptions';

/**
 * UnifiedCascadingSelect — UI-only component for risk-calculator dropdowns.
 *
 * Owns: input text, mode toggle (restricted vs show-all), mobile-sheet state.
 * Reads (never writes) options/loading/pagination from useCascadingOptions.
 *
 * What this component intentionally does NOT do (anymore):
 *   - manage `showAllOptions`, `showAllFetching`, `showAllPagination` state.
 *   - debounce inside callbacks (we debounce the VALUE via useDebouncedValue).
 *   - track in-flight requests / sequence numbers / phantom-event flags.
 *   - reset state in effects when the cascade or mode changes.
 *
 * All of that is RQ's job — see useCascadingOptions.js for the rationale.
 *
 * Bug killed by this rewrite (SP-408, May 2026):
 *   « The user types `aniy`, the list filters down to zero matches, the
 *     input mysteriously empties itself while the dropdown still shows the
 *     filtered (empty) state. »
 * Root cause was the previous file's manual handling of show-all mode + a
 * fragile suppress-empty-onSearch latch that occasionally missed an AntD
 * phantom event and wiped the controlled searchValue. The fix is structural:
 * one controlled input, one debounced value, no suppression logic.
 */

const DEBOUNCE_MS = 200;
const LIST_HEIGHT = 360; // ~10 rows on desktop (SP-408 rule 7)

const TOGGLE_LABELS = {
  treatment_type: {
    showAll: 'show_all_treatment_types',
    showFiltered: 'show_filtered_treatment_types',
  },
  application_technique: {
    showAll: 'show_all_techniques',
    showFiltered: 'show_filtered_techniques',
  },
  products: {
    showAll: 'show_all_products',
    showFiltered: 'show_filtered_products',
  },
  units: {
    showAll: 'show_all_units',
    showFiltered: 'show_filtered_units',
  },
};

const TOGGLE_LABEL_FALLBACKS = {
  treatment_type: {
    showAll: 'Voir tous les types de traitement',
    showFiltered: 'Voir les types correspondants aux critères',
  },
  application_technique: {
    showAll: "Voir toutes les techniques d'application",
    showFiltered: 'Voir les techniques correspondantes aux critères',
  },
  products: {
    showAll: 'Voir tous les produits',
    showFiltered: 'Voir les produits correspondants aux critères',
  },
  units: {
    showAll: 'Voir toutes les unités',
    showFiltered: 'Voir les unités correspondantes aux critères',
  },
};

// SP-408 rule 55: Bio / Fin pills shown to the right of the product label.
const renderBadges = (badges) => {
  if (!badges || badges.length === 0) return null;
  const colors = {
    Bio: { bg: '#E7F7E0', fg: '#207D55' },
    Fin: { bg: '#FFF1F0', fg: '#CF1322' },
    Nouveau: { bg: 'rgb(8, 151, 156)', fg: '#FFFFFF' },
  };
  return (
    <span style={{ display: 'inline-flex', gap: 4, marginLeft: 8 }}>
      {badges.map((b) => {
        const c = colors[b] || { bg: '#F0F0F0', fg: '#595959' };
        return (
          <span
            key={b}
            style={{
              backgroundColor: c.bg,
              color: c.fg,
              fontSize: 10,
              fontWeight: 600,
              padding: '1px 6px',
              borderRadius: 4,
              lineHeight: '16px',
            }}
          >
            {b}
          </span>
        );
      })}
    </span>
  );
};

const UnifiedCascadingSelect = ({
  type,
  cultures = [],
  treatmentType = null,
  applicationTechnique = null,
  productId = null,
  value,
  onChange,
  placeholder,
  label,
  disabled = false,
  allowClear = true,
  enrichedLabel = null,
  onMismatchChange,
  allowShowAll = false,
  ...otherProps
}) => {
  const { t } = useTranslation('riskCalculator');
  const isMobile = useIsMobile();

  // ---------------------------------------------------------------------------
  // UI state — five slots, no refs except the mismatch-callback latch
  // ---------------------------------------------------------------------------
  const [inputValue, setInputValue] = useState(''); // controlled desktop input
  const [showAllMode, setShowAllMode] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState('');

  // Ref to the AntD <Select> so we can restore focus after the auto-switch
  // from restricted → show-all. The options-array swap that follows the mode
  // toggle re-renders rc-select's internal selector, which can drop focus
  // from the search input even though our controlled `open` stays true.
  const selectRef = useRef(null);

  const activeRawInput = isMobile ? mobileSearchValue : inputValue;
  const debouncedQuery = useDebouncedValue(activeRawInput, DEBOUNCE_MS);

  // ---------------------------------------------------------------------------
  // Data fetching — fully delegated to React Query
  // ---------------------------------------------------------------------------
  const {
    options,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
    hasNoResults,
    isEnabled,
  } = useCascadingOptions({
    step: type,
    cultures,
    treatmentType,
    applicationTechnique,
    // SP-408 rule 75/77: only the units step uses productId.
    productId: type === 'units' ? productId : null,
    query: showAllMode ? debouncedQuery : '',
    showAll: showAllMode,
    enabled: !disabled,
  });

  // ---------------------------------------------------------------------------
  // Visible options (desktop). In restricted mode the backend returns the
  // full cascade set; we filter client-side so the dropdown tracks the typed
  // text. In show-all mode the backend already filters, so we just pass
  // through.
  // ---------------------------------------------------------------------------
  const visibleOptions = useMemo(() => {
    if (showAllMode || !debouncedQuery) return options;
    const needle = debouncedQuery.toLowerCase();
    return options.filter((opt) => {
      const label = String(opt.label || '').toLowerCase();
      const name = String(opt.name || '').toLowerCase();
      const approval = String(opt.approval_number || '').toLowerCase();
      return (
        label.includes(needle) ||
        name.includes(needle) ||
        approval.includes(needle)
      );
    });
  }, [options, showAllMode, debouncedQuery]);

  // ---------------------------------------------------------------------------
  // SP-408 rule 54 — auto-switch to show-all when the typed query has no
  // restricted matches. DERIVED boolean, not an effect with refs/flags.
  // Uses `visibleOptions` (post-client-filter) for restricted mode so we can
  // detect "no match for typed text" without needing the backend to filter.
  // ---------------------------------------------------------------------------
  const shouldAutoSwitch =
    allowShowAll &&
    !showAllMode &&
    dropdownOpen &&
    !isFetching &&
    !!debouncedQuery &&
    visibleOptions.length === 0 &&
    isEnabled;

  useEffect(() => {
    if (shouldAutoSwitch) setShowAllMode(true);
  }, [shouldAutoSwitch]);

  // After a mode flip, restore focus to the search input. We skip the initial
  // mount (`hasMountedRef`) so we don't steal focus when the component first
  // renders. We also gate on `dropdownOpen` so toggling the mode while the
  // dropdown is closed (e.g., a stale cascade-driven reset) doesn't pop the
  // popup open. `requestAnimationFrame` lets rc-select finish its internal
  // commit before we call `focus()` — otherwise the focus call lands on a
  // node that's about to be replaced.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (!dropdownOpen) return;
    const id = requestAnimationFrame(() => {
      selectRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [showAllMode, dropdownOpen]);

  // When the upstream cascade changes (e.g. user goes back and swaps a
  // culture / treatment type), drop show-all so the next view shows the
  // refreshed restricted list. We intentionally do NOT touch `inputValue`:
  // RQ's key change refetches the right data, and clearing the typed text
  // here would re-introduce the very bug we're trying to kill.
  const cascadeSignature = `${cultures.join(',')}|${treatmentType || ''}|${applicationTechnique || ''}`;
  useEffect(() => {
    setShowAllMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cascadeSignature]);

  // ---------------------------------------------------------------------------
  // SP-408 rule 41/43 — group application techniques by place_category.
  // Groups are built from the *visible* options so typing narrows the
  // category headers too.
  // ---------------------------------------------------------------------------
  const groupedOptions = useMemo(() => {
    if (type !== 'application_technique') return null;
    if (!visibleOptions.length) return null;
    if (!visibleOptions.some((o) => o.group)) return null;
    const groups = new Map();
    for (const opt of visibleOptions) {
      const key = opt.group || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(opt);
    }
    return Array.from(groups.entries()).map(([groupLabel, children]) => ({
      label: groupLabel,
      title: groupLabel,
      options: children,
    }));
  }, [type, visibleOptions]);

  // ---------------------------------------------------------------------------
  // Status messages (mismatch / empty / error) — same shape as before.
  // ---------------------------------------------------------------------------
  const valueNotInOptions =
    !!value && !isFetching && !options.some((opt) => opt.value === value);

  const statusMessage = useMemo(() => {
    if (error) {
      return {
        type: 'error',
        text: t('cascading_error'),
        tooltip: t('cascading_error_tooltip'),
        canRetry: true,
      };
    }
    if (valueNotInOptions && !hasNoResults) {
      return {
        type: 'mismatch',
        text: t(`cascading_mismatch_${type}`, t('cascading_mismatch_default')),
        tooltip: t('cascading_mismatch_tooltip'),
        canRetry: false,
      };
    }
    if (hasNoResults && !showAllMode) {
      const typeHintNames = {
        treatment_type: t('cascading_empty_hint_treatment_type'),
        application_technique: t('cascading_empty_hint_application_technique'),
        products: t('cascading_empty_hint_products'),
        units: t('cascading_empty_hint_units'),
      };
      return {
        type: 'empty',
        text: t(`cascading_empty_${type}`, t('cascading_empty_default')),
        tooltip: t('cascading_empty_tooltip'),
        hint: allowShowAll
          ? t('cascading_empty_hint', { type: typeHintNames[type] })
          : null,
        canRetry: false,
      };
    }
    return null;
  }, [
    error,
    valueNotInOptions,
    hasNoResults,
    type,
    t,
    showAllMode,
    allowShowAll,
  ]);

  // Report mismatch to the parent step (e.g. so it can paint a warning).
  const prevMismatchRef = useRef(null);
  useEffect(() => {
    if (!onMismatchChange) return;
    const hasMismatch =
      statusMessage?.type === 'mismatch' || statusMessage?.type === 'empty';
    const mismatchKey = hasMismatch ? statusMessage.text : null;
    if (prevMismatchRef.current !== mismatchKey) {
      prevMismatchRef.current = mismatchKey;
      onMismatchChange(hasMismatch, mismatchKey);
    }
  }, [statusMessage, onMismatchChange]);

  // ---------------------------------------------------------------------------
  // Phantom-onSearch guard
  // ---------------------------------------------------------------------------
  // rc-select fires `onSearch('')` from internal effects we cannot opt out of:
  //   1. close-effect (BaseSelect/index.js:250-254) when mergedOpen flips false
  //      in single-select mode — fires even when our controlled `open` stays
  //      true, because rc-select's mergedOpen briefly desyncs during commits.
  //   2. options-swap reconciliation when `options` array reference changes
  //      drastically (e.g. restricted ~100 items → show-all full list).
  //
  // The reported symptom is the options-swap path: user types "//" (0 hits) →
  // auto-switch fires → setShowAllMode(true) → new RQ key → new (huge) options
  // array → rc-select's reconciliation fires onSearch('') → typed text wiped.
  //
  // The robust invariant: rc-select's `onSearch` is supposed to mirror what
  // the user TYPED. Typing reduces the input one char at a time (Backspace),
  // or grows it one char at a time. A jump from "abc" straight to "" cannot
  // come from typing — the only ways the user can produce that transition
  // are via the X clear button (which routes through `onClear` → `onChange`,
  // never `onSearch`) or via Ctrl+A Delete (rare; user can re-type).
  //
  // Therefore: any `onSearch('')` arriving when the previous `inputValue` was
  // longer than one character is a phantom — ignore it. This catches both
  // phantom paths above without resurrecting the brittle one-shot flag.
  const handleSearch = useCallback(
    (raw) => {
      if (raw === '' && inputValue.length > 1) {
        return; // phantom — multi-char → empty cannot come from typing
      }
      setInputValue(raw);
    },
    [inputValue]
  );

  const handleChange = useCallback(
    (newValue, option) => {
      // Single explicit clear of the typed text, mirroring AntD's
      // `autoClearSearchValue` for single-select. The whole reason the
      // old file had a phantom-empty guard was that this clear used to
      // run in three places — it now runs only here.
      setInputValue('');
      setDropdownOpen(false);
      onChange?.(newValue, option);
    },
    [onChange]
  );

  const handleDropdownVisibleChange = useCallback((visible) => {
    setDropdownOpen(visible);
    // Don't touch inputValue on close: AntD already collapses the input
    // visually, and resetting state here would cause a flash on reopen.
  }, []);

  const handleToggleShowAll = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowAllMode((prev) => !prev);
    setDropdownOpen(true);
  }, []);

  // Mobile sheet toggle (no event in MobileSelectSheet callback signature).
  const handleMobileToggleShowAll = useCallback(() => {
    setShowAllMode((prev) => !prev);
  }, []);

  // Mobile search input — controlled separately so the sheet preserves it.
  const handleMobileSearch = useCallback((rawOrEvent) => {
    const value =
      typeof rawOrEvent === 'string'
        ? rawOrEvent
        : (rawOrEvent?.target?.value ?? '');
    setMobileSearchValue(value);
  }, []);

  // ---------------------------------------------------------------------------
  // Infinite scroll (desktop dropdown popup).
  // ---------------------------------------------------------------------------
  const handlePopupScroll = useCallback(
    (event) => {
      if (!showAllMode || !hasNextPage || isFetchingNextPage) return;
      const { target } = event;
      const threshold = 50;
      if (
        target.scrollTop + target.clientHeight >=
        target.scrollHeight - threshold
      ) {
        fetchNextPage();
      }
    },
    [showAllMode, hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  // ---------------------------------------------------------------------------
  // Label rendering helpers.
  // ---------------------------------------------------------------------------
  // SP-408 rule 56 + R68 — product rows show name + Bio/Fin badges + hover
  // tooltip when truncated. Same render for restricted & show-all (single
  // source of truth, the old file had two copies).
  const productOptionRender = useCallback((option) => {
    const fullLabel = option.label || '';
    return (
      <Tooltip title={fullLabel} mouseEnterDelay={0.4} placement="top">
        <div style={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            {fullLabel}
          </span>
          {renderBadges(option.data?.badges || option.badges)}
        </div>
      </Tooltip>
    );
  }, []);

  const mobileProductOptionRender = useCallback(
    (option) => (
      <div
        className="mobile-option-content"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <div className="mobile-option-label" style={{ fontWeight: 500 }}>
          {option.label}
        </div>
        {renderBadges(option.badges)}
      </div>
    ),
    []
  );

  // Label on the COLLAPSED selector (i.e. once a value is picked) — show
  // the enriched label if the value isn't in the current options list
  // (e.g. mode toggled or cascade narrowed past the selection).
  const labelRender = useCallback(
    (props) => {
      const { label: propLabel, value: propValue } = props;
      const optionExists = options.some((opt) => opt.value === propValue);
      const fullLabel = optionExists
        ? propLabel || ''
        : enrichedLabel || propLabel || '';
      return (
        <Tooltip title={fullLabel} placement="topLeft">
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fullLabel}
          </span>
        </Tooltip>
      );
    },
    [options, enrichedLabel]
  );

  // ---------------------------------------------------------------------------
  // Derived display strings.
  // ---------------------------------------------------------------------------
  const toggleLabel = useMemo(() => {
    const keys = TOGGLE_LABELS[type];
    const fallbacks = TOGGLE_LABEL_FALLBACKS[type];
    if (!keys) return '';
    return showAllMode
      ? t(keys.showFiltered, fallbacks.showFiltered)
      : t(keys.showAll, fallbacks.showAll);
  }, [type, showAllMode, t]);

  const mobileDisplayValue = useMemo(() => {
    if (!value) return '';
    const opt = options.find((o) => o.value === value);
    return opt?.label || enrichedLabel || '';
  }, [value, options, enrichedLabel]);

  // ---------------------------------------------------------------------------
  // Build the Select props once.
  // ---------------------------------------------------------------------------
  const isUnits = type === 'units';
  const isProducts = type === 'products';

  const baseSelectProps = {
    value,
    onChange: handleChange,
    placeholder: statusMessage?.text || placeholder,
    loading: isFetching,
    disabled: disabled || (isFetching && options.length === 0),
    allowClear: allowClear && !isUnits,
    showSearch: true,
    filterOption: false, // SERVER is the only filter — no client-side parallel filter
    autoClearSearchValue: false, // R6 — typed text stays even when filtered list is empty
    onSearch: handleSearch,
    searchValue: inputValue,
    notFoundContent: isFetching ? (
      <Spin size="small" />
    ) : (
      t('cascading_no_result', 'Aucun résultat trouvé')
    ),
    listHeight: LIST_HEIGHT,
    options: groupedOptions || visibleOptions,
    style: isUnits
      ? { width: '100%', height: 44 }
      : { width: '100%', minHeight: 44 },
    labelRender,
    open: dropdownOpen,
    onDropdownVisibleChange: handleDropdownVisibleChange,
    onPopupScroll: handlePopupScroll,
    status: statusMessage?.type === 'error' ? 'error' : undefined,
    optionRender: isProducts
      ? productOptionRender
      : (option) => (
          <div
            title={option.label}
            style={{ width: '100%', whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {option.label}
          </div>
        ),
    // Unit labels carry a "(culture ou précision)" suffix that the narrow
    // unit column would clip: fixed popup width (px) wider than the selector.
    popupMatchSelectWidth: isUnits ? 420 : true,
  };

  // ---------------------------------------------------------------------------
  // Dropdown footer (R50 toggle + R54 infinite-scroll spinner).
  // ---------------------------------------------------------------------------
  const dropdownRender = useCallback(
    (menu) => (
      <>
        {menu}
        {showAllMode && isFetchingNextPage && options.length > 0 && (
          <div
            style={{
              padding: '8px',
              textAlign: 'center',
              borderTop: '1px solid #f0f0f0',
            }}
          >
            <Spin size="small" />
          </div>
        )}
        {allowShowAll && (
          <div
            style={{
              borderTop: '1px solid #f0f0f0',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: '#105235',
              fontSize: 13,
              fontWeight: 500,
              backgroundColor: '#fafafa',
            }}
            onMouseDown={(e) => {
              // Prevent the popup from closing before the click fires.
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={handleToggleShowAll}
          >
            {showAllMode ? (
              <MinusCircleOutlined style={{ fontSize: 16 }} />
            ) : (
              <PlusCircleOutlined style={{ fontSize: 16 }} />
            )}
            <span>{toggleLabel}</span>
          </div>
        )}
      </>
    ),
    [
      showAllMode,
      isFetchingNextPage,
      options.length,
      allowShowAll,
      handleToggleShowAll,
      toggleLabel,
    ]
  );

  // ---------------------------------------------------------------------------
  // Mobile render
  // ---------------------------------------------------------------------------
  if (isMobile) {
    return (
      <div className="w-full">
        {label && (
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            {label}
          </label>
        )}

        <div className="relative">
          <div
            onClick={() => !disabled && setSheetOpen(true)}
            style={{
              width: '100%',
              minHeight: 44,
              padding: '8px 12px',
              border: `1px solid ${statusMessage?.type === 'error' ? '#ff4d4f' : '#d9d9d9'}`,
              borderRadius: 6,
              backgroundColor: disabled ? '#f5f5f5' : '#fff',
              cursor: disabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: mobileDisplayValue ? '#262626' : '#bfbfbf',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {mobileDisplayValue || statusMessage?.text || placeholder}
            </span>
            {isFetching ? (
              <Spin size="small" style={{ marginLeft: 8 }} />
            ) : (
              <ChevronDown
                size={16}
                style={{ marginLeft: 8, color: '#bfbfbf', flexShrink: 0 }}
              />
            )}
          </div>

          <MobileSelectSheet
            isOpen={sheetOpen}
            onClose={() => setSheetOpen(false)}
            title={label || placeholder || t('select_option', 'Sélectionner')}
            options={options}
            value={value}
            onChange={(v, o) => {
              handleChange(v, o);
              setSheetOpen(false);
            }}
            loading={isFetching}
            // Restricted mode: leave onSearch undefined so MobileSelectSheet
            // filters its options client-side (matches the prior behaviour).
            // Show-all mode: parent owns the search → backend refetch.
            onSearch={showAllMode ? handleMobileSearch : undefined}
            onLoadMore={showAllMode ? fetchNextPage : undefined}
            hasMore={showAllMode && hasNextPage}
            optionRender={isProducts ? mobileProductOptionRender : undefined}
            showAllToggle={
              allowShowAll
                ? {
                    enabled: true,
                    label: toggleLabel,
                    active: showAllMode,
                    onClick: handleMobileToggleShowAll,
                  }
                : undefined
            }
            placeholder={t('search_placeholder', 'Rechercher...')}
          />

          {renderStatusMessage(statusMessage, refetch, t)}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Desktop render
  // ---------------------------------------------------------------------------
  return (
    <div className="w-full">
      {label && (
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          {label}
        </label>
      )}

      <div className="relative">
        <Select
          ref={selectRef}
          {...baseSelectProps}
          dropdownRender={allowShowAll ? dropdownRender : undefined}
          {...otherProps}
        />

        {renderStatusMessage(statusMessage, refetch, t)}
      </div>
    </div>
  );
};

// Render helper — keeps mobile + desktop branches identical for the status row.
function renderStatusMessage(statusMessage, retry, t) {
  if (!statusMessage) return null;
  return (
    <div style={{ marginTop: 2 }}>
      <div
        className="flex items-center gap-1"
        style={{
          fontSize: 11,
          color: statusMessage.type === 'error' ? '#ff4d4f' : '#d48806',
          fontStyle: 'italic',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <ExclamationCircleOutlined style={{ fontSize: 11, flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {statusMessage.text}
        </span>
        {statusMessage.canRetry && (
          <button
            onClick={retry}
            style={{
              marginLeft: 4,
              padding: '0 6px',
              fontSize: 10,
              border: 'none',
              background: 'none',
              color: '#1677ff',
              cursor: 'pointer',
              textDecoration: 'underline',
              flexShrink: 0,
            }}
          >
            {t('cascading_retry')}
          </button>
        )}
      </div>
      {statusMessage.hint && (
        <div
          style={{
            fontSize: 10,
            color: '#595959',
            fontStyle: 'italic',
            marginTop: 2,
          }}
        >
          {statusMessage.hint}
        </div>
      )}
    </div>
  );
}

UnifiedCascadingSelect.propTypes = {
  type: PropTypes.oneOf([
    'treatment_type',
    'application_technique',
    'products',
    'units',
  ]).isRequired,
  cultures: PropTypes.arrayOf(PropTypes.string),
  treatmentType: PropTypes.string,
  applicationTechnique: PropTypes.string,
  productId: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool,
  enrichedLabel: PropTypes.string,
  onMismatchChange: PropTypes.func,
  allowShowAll: PropTypes.bool,
};

export default React.memo(UnifiedCascadingSelect);
